import { produce } from 'immer';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { distinctUntilChanged } from 'rxjs';
import { useStream } from '../../hooks/useStream';
import { GridConfig, StageViewModeEnum, Widget, WidgetData, WidgetTypeEnum } from '../../models/Index';
import Dashboard from '../../pages/dashboard/Dashboard';
import StateService from '../../services/stateService/StateService';
import UtilService from '../../services/utilService/UtilService';
import WidgetWrapper from '../widget-wrapper/WidgetWrapper';
import { useStyles } from './styles';

type Props = {
    gridConfig?: GridConfig;
    widgetData: WidgetData[];
    openSettings: (any) => void;
    removeWidget: (any) => void;
};

const ResponsiveReactGridLayout = WidthProvider(Responsive);

function Grid(props: Props) {
    const classes = useStyles();
    const bodyTheme = localStorage.getItem('theme');
    const [widgets, setWidgets] = useStream(StateService.widgets$, distinctUntilChanged());
    const [selectedStage, setSelectedStage] = useStream(StateService.selectedStage$, distinctUntilChanged());
    const [savedStage, setSavedStage] = useStream(StateService.savedStage$, distinctUntilChanged());
    const [stageViewMode, setStageViewMode] = useStream(StateService.stageViewMode$, distinctUntilChanged());
    const [isNavOpen, setIsNavOpen] = useStream(StateService.openNavigation$, distinctUntilChanged());
    const minSize = { w: 1, h: 2 };

    const manualUpdateWidgetData = (item: any) => {
        const updatedItem = { ...item }; // Create a new object based on the original item

        if (updatedItem && bodyTheme === 'dark' && updatedItem.backgroundColor === '#ffffff') {
            updatedItem.backgroundColor = '#353131';
        }
        if (updatedItem && bodyTheme === 'light' && updatedItem.backgroundColor === '#353131') {
            updatedItem.backgroundColor = '#ffffff';
        }
        if (updatedItem.titleFontSize === '1.3rem') {
            updatedItem.titleFontSize = '1.5rem';
        }
        if (bodyTheme === 'dark' && updatedItem.fontColor === '#070007') {
            updatedItem.fontColor = '#ffffff';
        }
        if (bodyTheme === 'light' && updatedItem.fontColor === '#ffffff') {
            updatedItem.fontColor = '#070007';
        }

        return updatedItem;
    };

    const layoutChange = useCallback(
        (layout: Widget[]) => {
            if (selectedStage?.stageId) {
                if (!isEqual(layout, widgets) && [StageViewModeEnum.WORK_IN_PROGRESS].includes(stageViewMode)) {
                    const updatedLayout = produce(selectedStage, (draft) => {
                        // Enforce minimum size for each widget in the layout
                        draft.workInProgressGridConfig.layouts.lg = layout;
                        draft.workInProgressWidgetData = props.widgetData;
                    });
                    setSelectedStage(updatedLayout);
                }
            }
        },
        [selectedStage, widgets, props.widgetData],
    );

    const handleOnResize = (layout, oldItem, newItem, placeholder: React.ReactNode, e: MouseEvent, element: HTMLElement) => {
        // Find the resized item in the layout array
        const resizedItem = layout.find((item) => item.i === newItem.i);

        // Check if the resized item's i matches a key in widgetData
        const matchingWidget = props.widgetData.find((widget) => widget.key === resizedItem.i);

        // Check if the widget is of type 'RSS_FEED', 'FREE_TEXT' or 'SUPERSET_DASHBOARD' and handle resizing
        if (matchingWidget && ['RSS_FEED', 'FREE_TEXT'].includes(matchingWidget.type)) {
            // Reset the newItem to oldItem to prevent resizing
            newItem = oldItem;
        } else if (matchingWidget && matchingWidget.type === WidgetTypeEnum.SUPERSET_DASHBOARD) {
            // Enforce minimum size for Superset dashboards
            if (newItem.w < 12) newItem.w = 12;
            if (newItem.h < 10.5) newItem.h = 10.5;

            // Reset the newItem to oldItem to prevent resizing
            newItem = oldItem;
        } else {
            // Enforce the minimum size during the resize action for non-RSS feed widgets
            if (newItem.w < minSize.w) {
                newItem.w = minSize.w;
            }
            if (newItem.h < minSize.h) {
                newItem.h = minSize.h;
            }
        }

        // Only proceed if no matching widget with type 'RSS_FEED' is found
        findResizedWidget(layout, oldItem, newItem);
    };

    // Function that finds the widget in the layout that has been resized
    const findResizedWidget = (layout, oldItem, newItem) => {
        const resizedItem = layout.find((item) => item.i === newItem.i);
        return resizedItem;
    };

    useEffect(() => {
        const checkForUnsavedChanges = (): boolean => {
            return UtilService.areUnsavedChangesPresent(selectedStage, savedStage);
        };

        StateService.changesMade$.next(checkForUnsavedChanges());
    }, [savedStage, selectedStage]);

    return (
        <>
            <ResponsiveReactGridLayout {...props.gridConfig} onLayoutChange={layoutChange} onResize={handleOnResize}>
                {props.widgetData?.map((item) => {
                    return (
                        <div className={classes.root} key={item?.key}>
                            <WidgetWrapper
                                widgetData={manualUpdateWidgetData(item)}
                                openSettings={props.openSettings}
                                removeWidget={props.removeWidget}
                            />
                        </div>
                    );
                })}
            </ResponsiveReactGridLayout>
        </>
    );
}

export default Grid;
